/**
 * Resolves what a person pasted into one identifier: `v=` always wins, radio
 * lists are songs never albums, only a pure `/playlist?list=` is an album,
 * FAST URLs and bare f_ids resolve to entities.
 *
 * Ingestion only accepts a YouTube channel id, so most of what this returns
 * exists to be REFUSED with a specific reason (see channelIdFrom). Telling
 * someone "that is a song link" beats "invalid input".
 *
 * Dependency-free on purpose: no React, no imports, no ambient state.
 */

const BARE_F_ID_RE = /^[a-z]_[0-9a-f]{6,}$/

// A YT Music artist browseId shares this shape, so both land as 'channelId'.
const CHANNEL_ID_RE = /^UC[0-9A-Za-z_-]{22}$/

// YouTube Music album browse ids.
const ALBUM_BROWSE_ID_RE = /^MPRE[0-9A-Za-z_-]+$/

// A radio/mix list is a generated queue, never a release. RDCLAK5uy_ is
// deliberately excluded: that is YouTube Music's prefix for its own CURATED
// playlists, which are a playlist somebody made - not a radio queue, and not
// a release either.
const RADIO_LIST_RE = /^(RD(?!CLAK)|UL|LL|LM)/

// Only these shapes name a RELEASE. A PL... list is a playlist somebody made;
// filing one as an album would put an arbitrary mixtape in the album queue.
const ALBUM_PLAYLIST_RE = /^(OLAK5uy_|MPRE)/

// Exactly 11 url-safe characters, or a truncated paste reads as valid.
const VIDEO_ID_RE = /^[0-9A-Za-z_-]{11}$/

// A bare @handle, which the Artist field's own placeholder invites.
const BARE_HANDLE_RE = /^@[A-Za-z0-9._-]{3,30}$/

// Looser than BARE_HANDLE_RE - a real handle can be non-ASCII (@日本語), and
// the @ prefix already identifies it. This only refuses the empty and absurd.
const HANDLE_BODY_RE = /^[^\s/?#]{3,30}$/u

// What FAST's own /artist/:artist_name route is keyed by.
const ARTIST_SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/

// Zero-width characters ride along on a copy-paste from Slack or Docs and
// produce an id that looks right and 404s. trim() does not remove them.
const INVISIBLE_RE = /[\u00AD\u200B-\u200D\u2060\uFEFF]/g

const YOUTUBE_HOSTS = new Set([
  'youtube.com',
  'www.youtube.com',
  'm.youtube.com',
  'music.youtube.com',
  'm.music.youtube.com',
  'youtu.be',
  'youtube-nocookie.com',
  'www.youtube-nocookie.com',
])

const FAST_HOSTS = new Set(['fast.feelsmusic.app', 'feelsmusic.app', 'www.feelsmusic.app'])

const FAST_ENTITY_SEGMENTS = new Set(['song', 'album', 'artist'])

/** Machine codes, not screen copy - each surface maps them to its own words. */
export const RESOLVE_REASONS = Object.freeze({
  EMPTY: 'empty',
  NOT_A_URL: 'not_a_url',
  UNKNOWN_HOST: 'unknown_host',
  RADIO_LIST_NO_VIDEO: 'radio_list_no_video',
  PLAYLIST_NOT_A_RELEASE: 'playlist_not_a_release',
  NO_IDENTIFIER: 'no_identifier',
  // A NAME, not a link. Its own code because the honest answer is specific:
  // picking an artist by name is V2 (a typeahead over the artist list). For
  // now the form takes links, channel ids and slugs, and the message should
  // say that instead of pretending the input was unreadable.
  LOOKS_LIKE_A_NAME: 'looks_like_a_name',
  // A legacy /user/ or /c/ URL - a different namespace from an @handle, and
  // nothing downstream can look one up.
  LEGACY_CHANNEL_URL: 'legacy_channel_url',
})

function fail(reason, input) {
  return { ok: false, reason, target: null, hints: {}, input }
}

function hit(type, idType, idValue, input, hints) {
  return {
    ok: true,
    reason: null,
    target: { type, idType, idValue },
    hints: hints || {},
    input,
  }
}

function parseUrl(raw) {
  // A human pastes `youtube.com/watch?v=x` as often as the https form.
  const candidate = /^[a-z][a-z0-9+.-]*:\/\//i.test(raw) ? raw : `https://${raw}`
  try {
    return new URL(candidate)
  } catch (e) {
    return null
  }
}

function decodeHandle(value) {
  try {
    return decodeURIComponent(value)
  } catch (e) {
    return value
  }
}

function resolveBareId(raw) {
  if (BARE_F_ID_RE.test(raw)) return hit('entity', 'f_id', raw, raw)
  if (CHANNEL_ID_RE.test(raw)) return hit('artist', 'channelId', raw, raw)
  if (ALBUM_BROWSE_ID_RE.test(raw)) return hit('album', 'browseId', raw, raw)
  if (BARE_HANDLE_RE.test(raw)) return hit('artist', 'handle', decodeHandle(raw.slice(1)), raw)
  return null
}

function resolveFastUrl(url, raw) {
  const segments = url.pathname.split('/').filter(Boolean)

  // An f_id anywhere in the path wins - it is the least ambiguous thing a
  // FAST url can carry.
  for (let i = 0; i < segments.length; i += 1) {
    if (!BARE_F_ID_RE.test(segments[i])) continue
    const preceding = i > 0 ? segments[i - 1] : null
    const type = preceding && FAST_ENTITY_SEGMENTS.has(preceding) ? preceding : 'entity'
    return hit(type, 'f_id', segments[i], raw)
  }

  // /artist/<slug> is FAST's REAL artist route (Router.jsx uses
  // :artist_name, not an f_id), so a link copied from an artist page carries
  // a slug and nothing else. That is the second identifier the backend
  // accepts for an artist.
  if (segments[0] === 'artist' && segments[1]) {
    const slug = decodeHandle(segments[1])
    if (ARTIST_SLUG_RE.test(slug)) return hit('artist', 'slug', slug, raw)
  }

  return fail(RESOLVE_REASONS.NO_IDENTIFIER, raw)
}

function resolveYouTubeUrl(url, raw) {
  const segments = url.pathname.split('/').filter(Boolean)
  const params = url.searchParams
  const list = params.get('list')
  const isRadio = Boolean((list && RADIO_LIST_RE.test(list)) || params.get('start_radio') === '1')

  // youtu.be/<videoId> carries the id in the path, not a `v` param. The
  // segment is shape-checked: taking it unconditionally turned
  // youtu.be/watch?v=X into the videoId "watch".
  if (url.hostname.endsWith('youtu.be')) {
    const first = segments[0]
    if (!first || !VIDEO_ID_RE.test(first)) return fail(RESOLVE_REASONS.NO_IDENTIFIER, raw)
    const shortHints = {}
    if (list) shortHints.playlistId = list
    if (isRadio) shortHints.fromRadioQueue = true
    return hit('song', 'videoId', first, raw, shortHints)
  }

  // RULE 1, and it outranks everything below it: `v=` ALWAYS wins. A watch
  // URL opened from inside a radio queue or an album still names one song,
  // so the list rides along as a hint and never as the answer.
  const videoId = params.get('v')
  if (videoId && !VIDEO_ID_RE.test(videoId)) return fail(RESOLVE_REASONS.NO_IDENTIFIER, raw)
  if (videoId) {
    const hints = {}
    if (list) hints.playlistId = list
    if (isRadio) hints.fromRadioQueue = true
    return hit('song', 'videoId', videoId, raw, hints)
  }

  // RULE 2: only a PURE /playlist?list= resolves as a release.
  if (segments[0] === 'playlist' && list) {
    if (isRadio) return fail(RESOLVE_REASONS.RADIO_LIST_NO_VIDEO, raw)
    // The plan's own words: "real playlist IDs are returned as hints only".
    if (!ALBUM_PLAYLIST_RE.test(list)) return fail(RESOLVE_REASONS.PLAYLIST_NOT_A_RELEASE, raw)
    return hit('album', 'audioPlaylistId', list, raw)
  }

  // Above the bare-list refusal: rule 1's "a video always wins" has to hold
  // when the id is in the path too. The list rides as a hint, as for ?v=.
  if (
    (segments[0] === 'shorts' || segments[0] === 'embed' || segments[0] === 'live' || segments[0] === 'v') &&
    segments[1] &&
    VIDEO_ID_RE.test(segments[1])
  ) {
    const hints = {}
    if (list) hints.playlistId = list
    if (isRadio) hints.fromRadioQueue = true
    return hit('song', 'videoId', segments[1], raw, hints)
  }

  // Channel forms also outrank a stray list param.
  if (segments[0] === 'channel' && segments[1] && CHANNEL_ID_RE.test(segments[1])) {
    return hit('artist', 'channelId', segments[1], raw)
  }

  // A list param on any other path is a hint that lost its song.
  if (list && !isRadio) return fail(RESOLVE_REASONS.PLAYLIST_NOT_A_RELEASE, raw)
  if (isRadio) return fail(RESOLVE_REASONS.RADIO_LIST_NO_VIDEO, raw)

  // RULE 3: channel forms. `/@handle` resolves to a handle - the crosswalk to
  // a channel id is a DB read this module must not do.
  if (segments[0] === 'channel' && segments[1]) {
    if (!CHANNEL_ID_RE.test(segments[1])) return fail(RESOLVE_REASONS.NO_IDENTIFIER, raw)
    return hit('artist', 'channelId', segments[1], raw)
  }
  if (segments[0] && segments[0].startsWith('@')) {
    // Shape-checked, or '/@' resolves ok with an empty idValue.
    const handle = decodeHandle(segments[0].slice(1))
    if (!HANDLE_BODY_RE.test(handle)) return fail(RESOLVE_REASONS.NO_IDENTIFIER, raw)
    return hit('artist', 'handle', handle, raw)
  }
  // /music-channel/ behaves like a handle; legacy /user/ and /c/ do not.
  if (segments[0] === 'music-channel' && segments[1]) {
    return hit('artist', 'handle', decodeHandle(segments[1]), raw)
  }
  if ((segments[0] === 'c' || segments[0] === 'user') && segments[1]) {
    return fail(RESOLVE_REASONS.LEGACY_CHANNEL_URL, raw)
  }

  // YouTube Music browse pages: /browse/UC… is an artist, /browse/MPRE… an album.
  if (segments[0] === 'browse' && segments[1]) {
    if (CHANNEL_ID_RE.test(segments[1])) return hit('artist', 'channelId', segments[1], raw)
    if (ALBUM_BROWSE_ID_RE.test(segments[1])) return hit('album', 'browseId', segments[1], raw)
  }

  return fail(RESOLVE_REASONS.NO_IDENTIFIER, raw)
}

/**
 * Resolve one pasted string into at most one identifier.
 *
 * Returns, always:
 *   { ok, reason, target: { type, idType, idValue } | null, hints, input }
 *
 * `type` is what the STRING names ('song' | 'album' | 'artist' | 'entity'),
 * never what the caller wanted. A caller that asked for a song and got an
 * artist back has a mismatch to report, not a resolver bug — which is the
 * whole reason this returns the found type rather than taking an expected
 * one as an argument.
 */
export function resolveIngestInput(raw) {
  const input = typeof raw === 'string' ? raw.replace(INVISIBLE_RE, '').trim() : ''
  if (!input) return fail(RESOLVE_REASONS.EMPTY, input)

  const bare = resolveBareId(input)
  if (bare) return bare

  // A name, before anything tries to parse it as a host.
  if (!/[./:?#]/.test(input) && (/\s/.test(input) || /[A-Z]/.test(input))) {
    return fail(RESOLVE_REASONS.LOOKS_LIKE_A_NAME, input)
  }

  // Before parseUrl: `https://sabrina-carpenter` parses as a hostname, so a
  // slug reaching the parser comes back as an unknown host.
  if (!/[./:?#\s]/.test(input) && ARTIST_SLUG_RE.test(input)) {
    return hit('artist', 'slug', input, input)
  }

  const url = parseUrl(input)
  if (!url) return fail(RESOLVE_REASONS.NOT_A_URL, input)

  const host = url.hostname.toLowerCase()
  if (FAST_HOSTS.has(host)) return resolveFastUrl(url, input)
  if (YOUTUBE_HOSTS.has(host)) return resolveYouTubeUrl(url, input)

  return fail(RESOLVE_REASONS.UNKNOWN_HOST, input)
}
